import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, RefreshCw, CheckCircle, XCircle, AlertCircle, Settings, ExternalLink, Edit2, Trash2, Plus, Server, Shield, Loader, ChevronDown, ChevronUp, Copy, Info, Mail, ChevronLeft } from 'lucide-react';
import Modal from '../../components/Modal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Get CNAME target: CloudFront domain if configured, otherwise server domain
const getCnameTarget = () => {
    const cloudfrontDomain = process.env.REACT_APP_CLOUDFRONT_DOMAIN;
    if (cloudfrontDomain) return cloudfrontDomain;
    const apiUrl = API_URL;
    const serverDomain = apiUrl.replace(/^https?:\/\//, '').replace(/\/api.*$/, '').split(':')[0];
    return serverDomain !== 'localhost' ? serverDomain : 'your-server-domain.com';
};

// Build copyable instructions for the admin to send to their customer (domain owner)
const buildCustomerInstructions = (event, subdomain, cnameTarget, acmRecord) => {
    let text = `DNS Setup Instructions for ${event.custom_hostname}\n\n`;
    text += `To connect your custom domain to the event booking page, add the following DNS record(s) in your DNS provider (e.g. Cloudflare, GoDaddy, Namecheap).\n\n`;
    text += `--- RECORD 1: Routing (required) ---\n`;
    text += `Type: CNAME\n`;
    text += `Name: ${subdomain}${subdomain === '@' ? ' (root domain)' : ' (subdomain)'}\n`;
    text += `Value/Target: ${cnameTarget}\n`;
    text += `TTL: 3600 (or default)\n\n`;
    if (acmRecord) {
        text += `--- RECORD 2: SSL Certificate Validation (required for HTTPS) ---\n`;
        text += `Type: CNAME\n`;
        text += `Name: ${acmRecord.name}\n`;
        text += `Value/Target: ${acmRecord.value}\n\n`;
        text += `GoDaddy users: For the Name field, enter only the part BEFORE your domain (e.g. _abc123.bookings for bookings.yourdomain.com). Do NOT include .yourdomain.com - GoDaddy adds it automatically. Otherwise the name will be duplicated and validation will fail.\n\n`;
    }
    text += `Notes:\n`;
    text += `- DNS changes can take up to 24-48 hours to propagate\n`;
    text += `- Both records must be added for secure HTTPS access\n`;
    text += `- Verify propagation: nslookup ${event.custom_hostname}\n`;
    return text;
};

export default function DomainManagementPage() {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [updatingNginx, setUpdatingNginx] = useState(false);
    const [syncingKvs, setSyncingKvs] = useState(false);
    const [nginxStatus, setNginxStatus] = useState(null);
    const [showDomainModal, setShowDomainModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [domainFormData, setDomainFormData] = useState({
        custom_hostname: '',
        autoGenerateCert: false
    });
    const [certificateStatuses, setCertificateStatuses] = useState({});
    const [checkingCerts, setCheckingCerts] = useState({});
    const [generatingCerts, setGeneratingCerts] = useState({});
    const [settingUpDomain, setSettingUpDomain] = useState({});
    const [applyingDomain, setApplyingDomain] = useState({});
    const [expandedDomains, setExpandedDomains] = useState({}); // key: hostname (lowercase) for domain-grouped view
    const [cnameTarget, setCnameTarget] = useState(getCnameTarget());
    const [copiedInstructionsFor, setCopiedInstructionsFor] = useState(null);
    const [showAddDomainModal, setShowAddDomainModal] = useState(false);
    const [addDomainHostname, setAddDomainHostname] = useState('');
    const [addDomainSubmitting, setAddDomainSubmitting] = useState(false);
    const [addDomainError, setAddDomainError] = useState('');

    // Group events by custom hostname (one domain = one SSL cert; multiple events can share a domain)
    const domainGroups = React.useMemo(() => {
        const map = {};
        events.forEach((e) => {
            const h = (e.custom_hostname || '').toLowerCase();
            if (!h) return;
            if (!map[h]) map[h] = { hostnameKey: h, hostname: e.custom_hostname, events: [] };
            map[h].events.push(e);
        });
        return Object.values(map);
    }, [events]);

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        // Fetch CloudFront domain from API if not set at build time
        if (!process.env.REACT_APP_CLOUDFRONT_DOMAIN) {
            fetch(`${API_URL}/config/cloudfront`)
                .then(res => res.ok ? res.json() : null)
                .then(config => {
                    if (config?.cloudfrontDomain) {
                        setCnameTarget(config.cloudfrontDomain);
                    }
                })
                .catch(() => {});
        }
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                // Filter to only show events with custom hostnames
                const eventsWithDomains = data.filter(e => e.custom_hostname);
                setEvents(eventsWithDomains);
                
                // Check certificate status for each event
                eventsWithDomains.forEach(event => {
                    if (event.custom_hostname) {
                        checkCertificateStatus(event.id, event.custom_hostname);
                    }
                });
            } else {
                setError('Failed to fetch events');
            }
        } catch (err) {
            setError(`Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const checkCertificateStatus = async (eventId, hostname) => {
        try {
            setCheckingCerts(prev => ({ ...prev, [eventId]: true }));
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${eventId}/ssl-certificate/status`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const status = await response.json();
                setCertificateStatuses(prev => ({ ...prev, [eventId]: status }));
            }
        } catch (err) {
            console.error('Error checking certificate status:', err);
        } finally {
            setCheckingCerts(prev => ({ ...prev, [eventId]: false }));
        }
    };

    const applyDomainToCloudFront = async (eventId) => {
        try {
            setApplyingDomain(prev => ({ ...prev, [eventId]: true }));
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${eventId}/domain/apply`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const result = await response.json();
                await checkCertificateStatus(eventId);
                alert(result.message || 'Domain applied to CloudFront successfully');
            } else {
                const err = await response.json();
                alert(`Apply failed: ${err.message || err.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setApplyingDomain(prev => ({ ...prev, [eventId]: false }));
        }
    };

    const setupDomain = async (eventId) => {
        try {
            setSettingUpDomain(prev => ({ ...prev, [eventId]: true }));
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${eventId}/domain/setup`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const result = await response.json();
                await checkCertificateStatus(eventId);
                alert(`Domain setup started. Add the DNS records below. ${result.acmValidationRecord ? 'Include the SSL validation record for HTTPS.' : ''}`);
            } else {
                const err = await response.json();
                alert(`Setup failed: ${err.message || err.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setSettingUpDomain(prev => ({ ...prev, [eventId]: false }));
        }
    };

    const generateCertificate = async (eventId, hostname) => {
        try {
            setGeneratingCerts(prev => ({ ...prev, [eventId]: true }));
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${eventId}/ssl-certificate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ autoRenew: true })
            });
            
            if (response.ok) {
                const result = await response.json();
                alert(`Certificate generation started for ${hostname}. This may take a few minutes.`);
                // Poll for status updates
                setTimeout(() => checkCertificateStatus(eventId, hostname), 10000);
            } else {
                const error = await response.json();
                alert(`Failed to generate certificate: ${error.error || error.message || 'Unknown error'}`);
            }
        } catch (err) {
            alert(`Error generating certificate: ${err.message}`);
        } finally {
            setGeneratingCerts(prev => ({ ...prev, [eventId]: false }));
        }
    };

    const syncCloudFrontKvs = async () => {
        try {
            setSyncingKvs(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/cloudfront-kvs/sync`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const result = await response.json();
                alert(result.message || 'CloudFront KVS synced successfully');
            } else {
                const err = await response.json();
                alert(`Sync failed: ${err.error || err.message || 'Unknown error'}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setSyncingKvs(false);
        }
    };

    const updateNginxConfig = async () => {
        try {
            setUpdatingNginx(true);
            setNginxStatus(null);
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/nginx-config/update`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const result = await response.json();
                if (result.warning) {
                    setNginxStatus({ 
                        success: true, 
                        message: result.message,
                        warning: result.warning,
                        path: result.path,
                        note: result.note
                    });
                } else {
                    setNginxStatus({ success: true, message: result.message });
                }
                setTimeout(() => setNginxStatus(null), 10000);
            } else {
                const error = await response.json();
                setNginxStatus({ success: false, message: error.error || error.message || 'Failed to update nginx config' });
            }
        } catch (err) {
            setNginxStatus({ success: false, message: `Error: ${err.message}` });
        } finally {
            setUpdatingNginx(false);
        }
    };

    const openAddDomainModal = () => {
        setAddDomainHostname('');
        setAddDomainError('');
        setShowAddDomainModal(true);
    };

    const handleAddCustomDomain = async () => {
        const hostname = addDomainHostname.trim();
        if (!hostname) {
            setAddDomainError('Please enter a domain (e.g. booking.example.com)');
            return;
        }
        if (hostname.includes(' ') || hostname.startsWith('http://') || hostname.startsWith('https://')) {
            setAddDomainError('Enter only the hostname, without spaces or protocol (e.g. booking.example.com)');
            return;
        }
        if (!hostname.includes('.')) {
            setAddDomainError('Please enter a valid domain with at least one dot (e.g. booking.example.com)');
            return;
        }
        setAddDomainError('');
        setAddDomainSubmitting(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: `Custom domain: ${hostname}`,
                    custom_hostname: hostname,
                    is_active: true,
                    is_public: true
                })
            });
            if (!response.ok) {
                const err = await response.json();
                setAddDomainError(err.error || err.message || 'Failed to add domain');
                return;
            }
            const newEvent = await response.json();
            setShowAddDomainModal(false);
            setAddDomainHostname('');
            await fetchEvents();
            const hostnameKey = (newEvent.custom_hostname || '').toLowerCase();
            if (hostnameKey) {
                setExpandedDomains(prev => ({ ...prev, [hostnameKey]: true }));
            }
        } catch (err) {
            setAddDomainError(err.message || 'Something went wrong');
        } finally {
            setAddDomainSubmitting(false);
        }
    };

    const handleEditDomain = (event) => {
        setEditingEvent(event);
        setDomainFormData({
            custom_hostname: event.custom_hostname || '',
            autoGenerateCert: false
        });
        setShowDomainModal(true);
    };

    const handleSaveDomain = async () => {
        if (!editingEvent) return;
        
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${editingEvent.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    custom_hostname: domainFormData.custom_hostname || null
                })
            });
            
            if (response.ok) {
                await fetchEvents();
                setShowDomainModal(false);
                setEditingEvent(null);
                
                // If auto-generate cert is enabled and hostname is set
                if (domainFormData.autoGenerateCert && domainFormData.custom_hostname) {
                    setTimeout(() => generateCertificate(editingEvent.id, domainFormData.custom_hostname), 2000);
                }
            } else {
                const error = await response.json();
                alert(`Failed to update domain: ${error.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleRemoveDomain = async (event) => {
        if (!window.confirm(`Are you sure you want to remove the custom domain "${event.custom_hostname}" from "${event.name}"?`)) {
            return;
        }
        
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${event.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    custom_hostname: null
                })
            });
            
            if (response.ok) {
                await fetchEvents();
            } else {
                const error = await response.json();
                alert(`Failed to remove domain: ${error.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const getCertificateStatusIcon = (status) => {
        if (!status) return <XCircle className="w-5 h-5 text-gray-400" />;
        // ACM certificate awaiting DNS validation
        if (status.source === 'acm' && status.acmStatus === 'PENDING_VALIDATION') {
            return <AlertCircle className="w-5 h-5 text-amber-500" />;
        }
        if (!status.exists) {
            return <XCircle className="w-5 h-5 text-gray-400" />;
        }
        if (status.valid && status.daysUntilExpiry > 30) {
            return <CheckCircle className="w-5 h-5 text-green-500" />;
        }
        if (status.valid && status.daysUntilExpiry > 0) {
            return <AlertCircle className="w-5 h-5 text-yellow-500" />;
        }
        return <XCircle className="w-5 h-5 text-red-500" />;
    };

    const getCertificateStatusText = (status) => {
        if (!status) return 'No Certificate';
        // ACM certificate requested but awaiting DNS validation
        if (status.source === 'acm' && status.acmStatus === 'PENDING_VALIDATION') {
            return 'Pending Validation';
        }
        if (!status.exists) {
            return 'No Certificate';
        }
        if (status.valid && status.daysUntilExpiry > 30) {
            return `Valid (${status.daysUntilExpiry} days remaining)`;
        }
        if (status.valid && status.daysUntilExpiry > 0) {
            return `Expiring Soon (${status.daysUntilExpiry} days)`;
        }
        return 'Expired';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Breadcrumb: Back to Events */}
                <nav className="mb-4" aria-label="Breadcrumb">
                    <button
                        type="button"
                        onClick={() => navigate('/events')}
                        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back to Events
                    </button>
                </nav>
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                                <Globe className="w-8 h-8" />
                                Domain Management
                            </h1>
                            <p className="text-gray-600 mt-2">Custom domains, DNS and SSL for event booking pages</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={openAddDomainModal}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                                title="Add a custom domain and configure DNS and SSL on this page"
                            >
                                <Plus className="w-4 h-4" />
                                Add custom domain
                            </button>
                            {cnameTarget.includes('cloudfront.net') && (
                                <button
                                    onClick={syncCloudFrontKvs}
                                    disabled={syncingKvs}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                                    title="Sync domain list after adding or changing domains"
                                >
                                    <RefreshCw className={`w-4 h-4 ${syncingKvs ? 'animate-spin' : ''}`} />
                                    {syncingKvs ? 'Syncing...' : 'Sync domains'}
                                </button>
                            )}
                            {!cnameTarget.includes('cloudfront.net') && (
                                <button
                                    onClick={updateNginxConfig}
                                    disabled={updatingNginx}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                                    title="Update nginx config with current domains"
                                >
                                    <RefreshCw className={`w-4 h-4 ${updatingNginx ? 'animate-spin' : ''}`} />
                                    {updatingNginx ? 'Updating...' : 'Update Nginx config'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Nginx Status */}
                    {nginxStatus && (
                        <div className={`mt-4 p-4 rounded-lg ${nginxStatus.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-start gap-2">
                                {nginxStatus.success ? (
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                                ) : (
                                    <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                                )}
                                <div className="flex-1">
                                    <div className={nginxStatus.success ? 'text-green-800' : 'text-red-800'}>
                                        {nginxStatus.message}
                                    </div>
                                    {nginxStatus.warning && (
                                        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                                            <div className="font-medium text-yellow-900 mb-1">⚠️ Warning:</div>
                                            <div className="text-yellow-800">{nginxStatus.warning}</div>
                                            {nginxStatus.path && (
                                                <div className="mt-2 text-xs">
                                                    <strong>Generated config location:</strong> <code className="bg-white px-1 rounded">{nginxStatus.path}</code>
                                                </div>
                                            )}
                                            {nginxStatus.note && (
                                                <div className="mt-1 text-xs text-yellow-700">{nginxStatus.note}</div>
                                            )}
                                        </div>
                                    )}
                                    {nginxStatus.error && (
                                        <div className="mt-2 text-sm text-red-700">
                                            <strong>Error:</strong> {nginxStatus.error}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Stats Cards - simplified (needs attention = domains with cert issues, not per-event) */}
                {(() => {
                    const uniqueDomains = [...new Set(events.map(e => (e.custom_hostname || '').toLowerCase()).filter(Boolean))];
                    const eventIdsByDomain = {};
                    events.forEach(e => {
                        const h = (e.custom_hostname || '').toLowerCase();
                        if (h && !eventIdsByDomain[h]) eventIdsByDomain[h] = e.id;
                    });
                    const needsAttention = uniqueDomains.filter(h => {
                        const s = certificateStatuses[eventIdsByDomain[h]];
                        return !s?.exists || !s?.valid || s?.daysUntilExpiry <= 30;
                    }).length;
                    return (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600">Domains</p>
                                        <p className="text-2xl font-bold text-gray-900">{uniqueDomains.length}</p>
                                    </div>
                                    <Globe className="w-8 h-8 text-blue-500" />
                                </div>
                            </div>
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600">Events</p>
                                        <p className="text-2xl font-bold text-gray-900">{events.length}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600">Needs attention</p>
                                        <p className="text-2xl font-bold text-gray-900">{needsAttention}</p>
                                    </div>
                                    {needsAttention > 0 && <AlertCircle className="w-8 h-8 text-yellow-500" />}
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Domains - grouped by domain (one SSL cert per domain) */}
                <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900">Custom Domains</h2>
                        <p className="text-sm text-gray-600 mt-1">Each domain has one SSL certificate. Events sharing a domain are listed under it. Expand for DNS and SSL steps.</p>
                    </div>

                    {domainGroups.length === 0 ? (
                        <div className="p-12 text-center">
                            <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">No events with custom domains configured</p>
                            <p className="text-sm text-gray-500 mt-2">Configure custom domains in the event configuration page</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {domainGroups.map((group) => {
                                const firstEvent = group.events[0];
                                // Certificate is per-domain: use best status among events (only one may have acm_certificate_arn set)
                                const certStatus = group.events.reduce((best, ev) => {
                                    const s = certificateStatuses[ev.id];
                                    if (!s) return best;
                                    if (!best) return s;
                                    if (s.valid && s.exists) return s;
                                    if (best.valid && best.exists) return best;
                                    if (s.exists) return s;
                                    if (best.exists) return best;
                                    return s.source === 'acm' ? s : best;
                                }, null) || certificateStatuses[firstEvent.id];
                                const isCheckingCert = group.events.some(ev => checkingCerts[ev.id]);
                                const isExpanded = expandedDomains[group.hostnameKey];
                                const hostnameParts = (group.hostname || '').split('.');
                                const subdomain = hostnameParts.length > 2 ? hostnameParts[0] : '@';
                                const copyToClipboard = (text) => { navigator.clipboard.writeText(text); };

                                return (
                                    <div key={group.hostnameKey} className="bg-white">
                                        {/* Domain header row */}
                                        <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedDomains(prev => ({ ...prev, [group.hostnameKey]: !prev[group.hostnameKey] }))}
                                                    className="text-gray-500 hover:text-gray-700 flex-shrink-0"
                                                    title={isExpanded ? 'Collapse' : 'Expand'}
                                                >
                                                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                                </button>
                                                <Globe className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                                <a
                                                    href={`https://${group.hostname}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-base font-medium text-blue-600 hover:text-blue-800 truncate"
                                                >
                                                    {group.hostname}
                                                    <ExternalLink className="w-4 h-4 inline ml-1" />
                                                </a>
                                                {group.events.length > 1 && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                                        {group.events.length} events
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <div className="flex items-center gap-2">
                                                    {isCheckingCert ? (
                                                        <Loader className="w-4 h-4 animate-spin text-gray-400" />
                                                    ) : (
                                                        getCertificateStatusIcon(certStatus)
                                                    )}
                                                    <span className="text-sm text-gray-700">
                                                        {isCheckingCert ? 'Checking...' : getCertificateStatusText(certStatus)}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => checkCertificateStatus(firstEvent.id, group.hostname)}
                                                    className="p-1.5 text-gray-500 hover:text-gray-700 rounded"
                                                    title="Check certificate"
                                                >
                                                    <RefreshCw className="w-4 h-4" />
                                                </button>
                                                {cnameTarget.includes('cloudfront.net') && (
                                                    <>
                                                        <button
                                                            onClick={() => setupDomain(firstEvent.id)}
                                                            disabled={settingUpDomain[firstEvent.id]}
                                                            className="p-1.5 text-indigo-600 hover:text-indigo-800 rounded disabled:opacity-50"
                                                            title="Setup domain (request ACM certificate)"
                                                        >
                                                            {settingUpDomain[firstEvent.id] ? <Loader className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                                                        </button>
                                                        {certStatus?.source === 'acm' && certStatus?.valid && (
                                                            <button
                                                                onClick={() => applyDomainToCloudFront(firstEvent.id)}
                                                                disabled={applyingDomain[firstEvent.id]}
                                                                className="p-1.5 text-green-600 hover:text-green-800 rounded disabled:opacity-50"
                                                                title="Apply to CloudFront (enable HTTPS)"
                                                            >
                                                                {applyingDomain[firstEvent.id] ? <Loader className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Events under this domain */}
                                        <div className="px-6 pb-3 pl-14">
                                            <ul className="space-y-1">
                                                {group.events.map((event) => (
                                                    <li key={event.id} className="flex items-center justify-between gap-2 py-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/events/${event.id}`)}
                                                            className="text-sm font-medium text-indigo-600 hover:text-indigo-800 text-left"
                                                        >
                                                            {event.name}
                                                        </button>
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => handleEditDomain(event)}
                                                                className="p-1 text-gray-500 hover:text-gray-700 rounded"
                                                                title="Edit domain for this event"
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleRemoveDomain(event)}
                                                                className="p-1 text-red-500 hover:text-red-700 rounded"
                                                                title="Remove domain from this event"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Expanded: DNS + SSL (per domain) */}
                                        {isExpanded && (
                                            <div className="px-6 pb-6 pl-14 pt-0 border-t border-gray-100 bg-gray-50">
                                                <div className="space-y-6 pt-4">
                                                    {/* Event URLs - one per event on this domain */}
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                                            <Globe className="w-4 h-4" />
                                                            Event URLs
                                                        </h4>
                                                        <div className="space-y-2">
                                                            {group.events.length === 1 ? (
                                                                <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between gap-2">
                                                                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded flex-1 truncate">
                                                                        https://{group.hostname}/
                                                                    </code>
                                                                    <button onClick={() => copyToClipboard(`https://${group.hostname}/`)} className="text-gray-500 hover:text-gray-700 flex-shrink-0" title="Copy"><Copy className="w-4 h-4" /></button>
                                                                </div>
                                                            ) : (
                                                                group.events.map((ev) => {
                                                                    const key = ev.event_key || ev.short_code || ev.slug;
                                                                    const url = `https://${group.hostname}/${key}`;
                                                                    return (
                                                                        <div key={ev.id} className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between gap-2">
                                                                            <div className="min-w-0">
                                                                                <div className="text-xs font-medium text-gray-600">{ev.name}</div>
                                                                                <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded block truncate">{url}</code>
                                                                            </div>
                                                                            <button onClick={() => copyToClipboard(url)} className="text-gray-500 hover:text-gray-700 flex-shrink-0" title="Copy"><Copy className="w-4 h-4" /></button>
                                                                        </div>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Instructions for Your Customer */}
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                                            <Mail className="w-4 h-4" />
                                                            Instructions for Your Customer
                                                        </h4>
                                                        <p className="text-sm text-gray-600 mb-3">
                                                            The domain owner must add the DNS records below. Share these instructions so they can configure their DNS provider.
                                                        </p>
                                                        <button
                                                            onClick={() => {
                                                                const acmRecord = certStatus?.acmValidationRecord;
                                                                const instructions = buildCustomerInstructions(firstEvent, subdomain, cnameTarget, acmRecord);
                                                                copyToClipboard(instructions);
                                                                setCopiedInstructionsFor(group.hostnameKey);
                                                                setTimeout(() => setCopiedInstructionsFor(null), 2000);
                                                            }}
                                                            className="mb-4 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 text-sm font-medium flex items-center gap-2"
                                                        >
                                                            <Copy className="w-4 h-4" />
                                                            {copiedInstructionsFor === group.hostnameKey ? 'Copied!' : 'Copy instructions to send to customer'}
                                                        </button>

                                                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                                                                        <div>
                                                                            <p className="text-sm font-medium text-blue-900 mb-2">Step 1: Add the routing CNAME record</p>
                                                                            <p className="text-xs text-blue-800 mb-2">This points their domain to the event booking page.</p>
                                                                            <div className="bg-white p-4 rounded border border-blue-200">
                                                                                <table className="w-full text-sm">
                                                                                    <tbody>
                                                                                        <tr>
                                                                                            <td className="py-1 text-blue-600 font-medium w-28">Type</td>
                                                                                            <td className="py-1">
                                                                                                <code className="bg-gray-100 px-2 py-0.5 rounded">CNAME</code>
                                                                                                <button onClick={() => copyToClipboard('CNAME')} className="ml-2 text-gray-500 hover:text-gray-700"><Copy className="w-3 h-3 inline" /></button>
                                                                                            </td>
                                                                                        </tr>
                                                                                        <tr>
                                                                                            <td className="py-1 text-blue-600 font-medium">Name</td>
                                                                                            <td className="py-1">
                                                                                                <code className="bg-gray-100 px-2 py-0.5 rounded">{subdomain}</code>
                                                                                                <button onClick={() => copyToClipboard(subdomain)} className="ml-2 text-gray-500 hover:text-gray-700"><Copy className="w-3 h-3 inline" /></button>
                                                                                                <span className="text-xs text-gray-500 ml-1">{subdomain === '@' ? '(root domain)' : '(subdomain)'}</span>
                                                                                            </td>
                                                                                        </tr>
                                                                                        <tr>
                                                                                            <td className="py-1 text-blue-600 font-medium">Value (Target)</td>
                                                                                            <td className="py-1">
                                                                                                <code className="bg-gray-100 px-2 py-0.5 rounded font-mono">{cnameTarget}</code>
                                                                                                <button onClick={() => copyToClipboard(cnameTarget)} className="ml-2 text-gray-500 hover:text-gray-700"><Copy className="w-3 h-3 inline" /></button>
                                                                                            </td>
                                                                                        </tr>
                                                                                        <tr>
                                                                                            <td className="py-1 text-blue-600 font-medium">TTL</td>
                                                                                            <td className="py-1"><code className="bg-gray-100 px-2 py-0.5 rounded">3600</code> (or default)</td>
                                                                                        </tr>
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>

                                                                        {certStatus?.acmValidationRecord && (
                                                                            <div>
                                                                                <p className="text-sm font-medium text-blue-900 mb-2">Step 2: Add the SSL validation CNAME record</p>
                                                                                <p className="text-xs text-blue-800 mb-2">This proves domain ownership so we can issue an HTTPS certificate. Both records are required for secure access.</p>
                                                                                <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3">
                                                                                    <p className="text-sm font-medium text-amber-900 mb-1">⚠️ GoDaddy users</p>
                                                                                    <p className="text-xs text-amber-800">
                                                                                        GoDaddy appends your domain automatically. For the <strong>Name</strong> field, enter only the part <em>before</em> your domain (e.g. <code>_abc123xyz.bookings</code> for <code>bookings.yourdomain.com</code>). Do not include <code>.yourdomain.com</code> or the zone name—otherwise it will be duplicated and validation will fail.
                                                                                    </p>
                                                                                </div>
                                                                                <div className="bg-white p-4 rounded border border-blue-200">
                                                                                    <table className="w-full text-sm">
                                                                                        <tbody>
                                                                                            <tr>
                                                                                                <td className="py-1 text-blue-600 font-medium w-28">Type</td>
                                                                                                <td className="py-1"><code className="bg-gray-100 px-2 py-0.5 rounded">CNAME</code></td>
                                                                                            </tr>
                                                                                            <tr>
                                                                                                <td className="py-1 text-blue-600 font-medium">Name</td>
                                                                                                <td className="py-1">
                                                                                                    <code className="bg-gray-100 px-2 py-0.5 rounded font-mono break-all">{certStatus.acmValidationRecord.name}</code>
                                                                                                    <button onClick={() => copyToClipboard(certStatus.acmValidationRecord.name)} className="ml-2 text-gray-500 hover:text-gray-700"><Copy className="w-3 h-3 inline" /></button>
                                                                                                </td>
                                                                                            </tr>
                                                                                            <tr>
                                                                                                <td className="py-1 text-blue-600 font-medium">Value (Target)</td>
                                                                                                <td className="py-1">
                                                                                                    <code className="bg-gray-100 px-2 py-0.5 rounded font-mono break-all">{certStatus.acmValidationRecord.value}</code>
                                                                                                    <button onClick={() => copyToClipboard(certStatus.acmValidationRecord.value)} className="ml-2 text-gray-500 hover:text-gray-700"><Copy className="w-3 h-3 inline" /></button>
                                                                                                </td>
                                                                                            </tr>
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        <div className="bg-blue-100 p-3 rounded text-xs">
                                                                            <p className="font-medium text-blue-900 mb-1">Where to add these records</p>
                                                                            <p className="text-blue-800 mb-1">Your customer adds these in their DNS provider (where they manage their domain). Common providers:</p>
                                                                            <ul className="list-disc list-inside text-blue-800 space-y-0.5">
                                                                                <li><strong>GoDaddy:</strong> My Products → DNS → Add → CNAME. For the SSL validation record Name field, enter only the part before your domain (e.g. <code>_abc123.bookings</code>)—do not include the zone name.</li>
                                                                                <li><strong>Cloudflare:</strong> DNS → Records → Add record</li>
                                                                                <li><strong>Namecheap:</strong> Advanced DNS → Add New Record → CNAME</li>
                                                                                <li><strong>AWS Route 53:</strong> Hosted Zones → Create Record</li>
                                                                            </ul>
                                                                        </div>
                                                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                                                            <p className="font-medium text-yellow-900 mb-1">Notes for your customer</p>
                                                                            <ul className="list-disc list-inside text-yellow-800 space-y-0.5">
                                                                                <li>DNS changes can take up to 24–48 hours to propagate</li>
                                                                                {certStatus?.acmValidationRecord ? (
                                                                                    <>
                                                                                        <li>Both records must be added for the custom domain to work with HTTPS</li>
                                                                                        <li>GoDaddy: For the SSL validation Name field, enter only the part before the domain (e.g. <code>_abc123.bookings</code>), not the full name—GoDaddy appends the zone automatically</li>
                                                                                    </>
                                                                                ) : (
                                                                                    <li>The routing record must be added first; SSL will be configured separately</li>
                                                                                )}
                                                                                <li>They can verify propagation with: <code className="bg-white px-1 rounded">nslookup {group.hostname}</code></li>
                                                                            </ul>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* SSL Certificate (AWS ACM) - one per domain */}
                                                                <div>
                                                                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                                                        <Shield className="w-4 h-4" />
                                                                        SSL Certificate (AWS ACM)
                                                                    </h4>
                                                                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                                        {isCheckingCert ? (
                                                                            <div className="text-sm text-gray-600">Checking certificate status...</div>
                                                                        ) : certStatus && certStatus.exists && certStatus.valid ? (
                                                                            <div className="space-y-3">
                                                                                <div className="flex items-center gap-2 text-green-600">
                                                                                    <CheckCircle className="w-5 h-5" />
                                                                                    <span className="font-medium">Certificate validated (ACM)</span>
                                                                                </div>
                                                                                {certStatus.source === 'acm' && cnameTarget.includes('cloudfront.net') && (
                                                                                    <div className="bg-indigo-50 border border-indigo-200 rounded p-3">
                                                                                        <p className="text-sm text-indigo-800 mb-2">Apply the certificate to CloudFront to enable HTTPS:</p>
                                                                                        <button
                                                                                            onClick={() => applyDomainToCloudFront(firstEvent.id)}
                                                                                            disabled={applyingDomain[firstEvent.id]}
                                                                                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                                                                                        >
                                                                                            {applyingDomain[firstEvent.id] ? (
                                                                                                <><Loader className="w-4 h-4 animate-spin" /> Applying...</>
                                                                                            ) : (
                                                                                                <><Shield className="w-4 h-4" /> Apply to CloudFront</>
                                                                                            )}
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                                {certStatus.daysUntilExpiry !== undefined && (
                                                                                    <div className="text-sm text-gray-700">
                                                                                        <p><strong>Expires:</strong> {certStatus.expiresAt ? new Date(certStatus.expiresAt).toLocaleDateString() : 'Unknown'}</p>
                                                                                        <p><strong>Days remaining:</strong> {certStatus.daysUntilExpiry}</p>
                                                                                        {certStatus.issuer && <p><strong>Issuer:</strong> {certStatus.issuer}</p>}
                                                                                    </div>
                                                                                )}
                                                                                {certStatus.daysUntilExpiry <= 30 && (
                                                                                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                                                                                        <p className="text-xs text-yellow-800">Certificate is expiring soon. Consider renewing it.</p>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="space-y-3">
                                                                                <div className="flex items-center gap-2 text-yellow-600">
                                                                                    <AlertCircle className="w-5 h-5" />
                                                                                    <span className="font-medium">
                                                                                        {certStatus?.source === 'acm' && certStatus?.acmStatus === 'PENDING_VALIDATION'
                                                                                            ? 'Certificate pending validation'
                                                                                            : 'No certificate'}
                                                                                    </span>
                                                                                </div>
                                                                                <p className="text-sm text-gray-700">
                                                                                    {certStatus?.source === 'acm' && certStatus?.acmStatus === 'PENDING_VALIDATION'
                                                                                        ? 'Add the DNS validation record above. Once ACM validates (usually 5–30 minutes), click Apply to CloudFront.'
                                                                                        : certStatus?.acmValidationRecord
                                                                                            ? 'Add the DNS validation record above. Once ACM validates, click Apply to CloudFront.'
                                                                                            : cnameTarget.includes('cloudfront.net')
                                                                                                ? 'Click Setup Domain to request an ACM certificate and add the domain to CloudFront.'
                                                                                                : 'Request an ACM certificate via Setup Domain (CloudFront) to enable HTTPS.'}
                                                                                </p>
                                                                                {cnameTarget.includes('cloudfront.net') && certStatus?.source !== 'acm' && (
                                                                                    <button
                                                                                        onClick={() => setupDomain(firstEvent.id)}
                                                                                        disabled={settingUpDomain[firstEvent.id]}
                                                                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                                                                                    >
                                                                                        {settingUpDomain[firstEvent.id] ? (
                                                                                            <><Loader className="w-4 h-4 animate-spin" /> Setting up...</>
                                                                                        ) : (
                                                                                            <><Globe className="w-4 h-4" /> Setup domain (request ACM certificate)</>
                                                                                        )}
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Nginx Configuration Info - only when not using CloudFront */}
                {!cnameTarget.includes('cloudfront.net') && (
                    <div className="mt-6 bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Server className="w-6 h-6" />
                            Nginx Configuration
                        </h2>
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h3 className="font-semibold text-blue-900 mb-2">Automatic Configuration</h3>
                                <p className="text-sm text-blue-800">
                                    Nginx configuration is updated when you add, modify, or remove custom domains. Use the "Update Nginx config" button above to refresh.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add Custom Domain Modal */}
            {showAddDomainModal && (
                <Modal
                    isOpen={showAddDomainModal}
                    onClose={() => {
                        if (!addDomainSubmitting) {
                            setShowAddDomainModal(false);
                            setAddDomainError('');
                        }
                    }}
                    title="Add Custom Domain"
                    size="md"
                >
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                            Enter the custom domain for your event booking page. A new event will be created with this domain so you can configure DNS and SSL below.
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Custom domain
                            </label>
                            <input
                                type="text"
                                value={addDomainHostname}
                                onChange={(e) => {
                                    setAddDomainHostname(e.target.value.trim());
                                    setAddDomainError('');
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddCustomDomain()}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g. booking.example.com"
                                disabled={addDomainSubmitting}
                                autoFocus
                            />
                            {addDomainError && (
                                <p className="text-sm text-red-600 mt-1">{addDomainError}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">Enter only the hostname, without https://</p>
                        </div>
                        <div className="flex justify-end gap-2 pt-4 border-t">
                            <button
                                onClick={() => {
                                    if (!addDomainSubmitting) {
                                        setShowAddDomainModal(false);
                                        setAddDomainError('');
                                    }
                                }}
                                disabled={addDomainSubmitting}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddCustomDomain}
                                disabled={addDomainSubmitting}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {addDomainSubmitting ? (
                                    <><Loader className="w-4 h-4 animate-spin" /> Adding...</>
                                ) : (
                                    <>Add and configure</>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Edit Domain Modal */}
            {showDomainModal && editingEvent && (
                <Modal
                    isOpen={showDomainModal}
                    onClose={() => {
                        setShowDomainModal(false);
                        setEditingEvent(null);
                    }}
                    title="Edit Custom Domain"
                >
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Custom Hostname / Domain
                            </label>
                            <input
                                type="text"
                                value={domainFormData.custom_hostname}
                                onChange={(e) => setDomainFormData({ ...domainFormData, custom_hostname: e.target.value.trim() })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="e.g., booking.bmw-dealership.com"
                            />
                            <p className="text-xs text-gray-500 mt-1">Leave empty to remove the custom domain</p>
                        </div>
                        
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="autoGenerateCert"
                                checked={domainFormData.autoGenerateCert}
                                onChange={(e) => setDomainFormData({ ...domainFormData, autoGenerateCert: e.target.checked })}
                                className="mr-2"
                            />
                            <label htmlFor="autoGenerateCert" className="text-sm text-gray-700">
                                Automatically generate SSL certificate when saving
                            </label>
                        </div>

                        <div className="flex justify-end gap-2 pt-4 border-t">
                            <button
                                onClick={() => {
                                    setShowDomainModal(false);
                                    setEditingEvent(null);
                                }}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveDomain}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

